/**
 * CarmViewService — C-arm angle view data management, selection, and persistence.
 *
 * Owns the list of CarmView presets (pre-computed from vascular description + user-created).
 * Tracks edits to pre-computed views and manages custom user views.
 *
 * Pure data service — has no viewport or camera dependency.
 * Camera binding is handled separately by CarmCameraBinding.
 *
 * Events:
 * - VIEWS_INITIALIZED: fired once when views are loaded from data
 * - SELECTION_CHANGED: fired whenever selected view changes (payload includes cameraTransform)
 * - VIEW_UPDATED: fired when a view's camera state is updated
 * - VIEW_ADDED: fired when a custom view is created
 * - VIEW_DELETED: fired when a custom view is deleted
 * - VIEW_RENAMED: fired when a view is renamed
 * - VIEW_RESET: fired when an edited pre-computed view is reset to original
 * - EDIT_STARTED: fired when a draft edit begins (create or update)
 * - EDIT_CHANGED: fired whenever the draft angles change (panel, 3D tool, 2D actor)
 * - EDIT_COMMITTED: fired after a draft is committed into a view
 * - EDIT_CANCELLED: fired when a draft is abandoned
 */

import { ViewportLayoutService } from './viewport-layout-service';
import { ViewportService } from './viewport-service';
import { PubSubService } from '../core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '../core/types';
import { clampCarmAngles } from '../domain/carm-angles';
import type { CarmAngles } from '../domain/carm-angles';
import { CarmView, cameraTransformFromState } from '../domain/carm-view';
import type { ICameraTransform } from '../domain/carm-view';
import type { ICarmViewCustomDTO, ICarmViewEditDTO } from '../domain/sizing/types';
import type { CameraState } from '../viewports/types';

/** Edit target: index of an existing view to update, or 'new' to create one. */
export type CarmEditTarget = number | 'new';

interface CarmEditDraft {
    target: CarmEditTarget;
    angles: CarmAngles;
    baseline: CarmAngles | null;
}

const DIRTY_ANGLE_EPSILON_DEG = 1e-6;

export class CarmViewService extends PubSubService {
    static readonly SERVICE_NAME = 'CarmViewService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: CarmViewService.SERVICE_NAME,
        factory: (context: ServiceContext) => new CarmViewService(context),
    };

    readonly EVENTS = {
        VIEWS_INITIALIZED: 'carm:views:initialized',
        SELECTION_CHANGED: 'carm:selection:changed',
        VIEW_UPDATED: 'carm:view:updated',
        VIEW_ADDED: 'carm:view:added',
        VIEW_DELETED: 'carm:view:deleted',
        VIEW_RENAMED: 'carm:view:renamed',
        VIEW_RESET: 'carm:view:reset',
        EDIT_STARTED: 'carm:edit:started',
        EDIT_CHANGED: 'carm:edit:changed',
        EDIT_COMMIT_REQUESTED: 'carm:edit:commit-requested',
        EDIT_COMMITTED: 'carm:edit:committed',
        EDIT_CANCELLED: 'carm:edit:cancelled',
    } as const;

    /** Original pre-computed views from vascular description (immutable reference). */
    private _originalViews: CarmView[] = [];
    /** Current views: pre-computed (possibly edited) + custom appended. */
    private _views: CarmView[] = [];
    /** Number of pre-computed views (first N entries in _views). */
    private _precomputedCount = 0;
    /** Indices of pre-computed views that have been edited by the user. */
    private _editedIndices: Set<number> = new Set();
    /** Auto-increment counter for custom view naming. */
    private _customCounter = 0;

    private _selectedIndex: number | null = null;

    /** Active edit draft, or null outside C-arm edit mode. */
    private _draft: CarmEditDraft | null = null;

    /** Sibling-service subscriptions active only while a draft exists. */
    private _editInterruptionUnsubs: Array<() => void> = [];

    private readonly context: ServiceContext;

    constructor(context: ServiceContext) {
        super();
        this.context = context;
    }

    // ========================================================================
    // Initialization
    // ========================================================================

    initialize(views: CarmView[]): void {
        this.cancelEdit();
        this._originalViews = [...views];
        this._views = [...views];
        this._precomputedCount = views.length;
        this._editedIndices.clear();
        this._customCounter = 0;
        this._selectedIndex = null;
        this.broadcast(this.EVENTS.VIEWS_INITIALIZED, undefined);
    }

    // ========================================================================
    // Accessors
    // ========================================================================

    getViews(): readonly CarmView[] {
        return [...this._views];
    }

    getSelectedIndex(): number | null {
        return this._selectedIndex;
    }

    /** Whether the view at this index is a pre-computed view (vs custom). */
    isPrecomputed(index: number): boolean {
        return index < this._precomputedCount;
    }

    /** Whether a pre-computed view has been edited by the user. */
    isEdited(index: number): boolean {
        return this._editedIndices.has(index);
    }

    // ========================================================================
    // Selection
    // ========================================================================

    /**
     * Deselect the current view. Views are only ever selected through
     * addView's auto-select — clicking a preset row starts an edit instead.
     */
    deselectView(): void {
        this.cancelEdit();
        if (this._selectedIndex === null) return;

        this._selectedIndex = null;

        this.broadcast(this.EVENTS.SELECTION_CHANGED, {
            selectedIndex: null,
            cameraTransform: null,
        });
    }

    // ========================================================================
    // Edit mode (draft)
    // ========================================================================

    /**
     * Begin a draft edit. Deselects any selected view (mutual exclusion) and
     * cancels a previous draft. The camera side (snap into the envelope,
     * initial draft angles for 'new') is handled by CarmCameraBinding on
     * EDIT_STARTED.
     */
    beginEdit(target: CarmEditTarget): void {
        if (typeof target === 'number' && !this._views[target]) {
            console.warn(`[CarmViewService] beginEdit: no view at index ${target}`);
            return;
        }

        this.cancelEdit();
        this.deselectView();

        const view = typeof target === 'number' ? this._views[target] : null;
        const angles = view
            ? clampCarmAngles({ craniocaudal: view.craniocaudal, oblique: view.oblique })
            : // Placeholder for 'new' — the binding derives the real angles
              // from the live camera and pushes them via setDraftAngles
              { craniocaudal: 0, oblique: 0 };

        this._draft = { target, angles, baseline: view ? angles : null };
        this.subscribeEditInterruptions();
        this.broadcast(this.EVENTS.EDIT_STARTED, { target } satisfies CarmEditStartedPayload);
    }

    /**
     * Update the draft angles (from the panel chevrons, the constrained 3D
     * tool, or the 2D rotation actor). Always clamps into the C-arm envelope.
     */
    setDraftAngles(angles: CarmAngles): void {
        if (!this._draft) return;

        const clamped = clampCarmAngles(angles);
        this._draft = {
            ...this._draft,
            angles: clamped,
            baseline: this._draft.baseline ?? clamped,
        };
        this.broadcast(this.EVENTS.EDIT_CHANGED, {
            angles: this._draft.angles,
        } satisfies CarmEditChangedPayload);
    }

    getDraft(): { target: CarmEditTarget; angles: CarmAngles } | null {
        return this._draft ? { target: this._draft.target, angles: this._draft.angles } : null;
    }

    /**
     * Whether the draft holds unsaved user changes — the question a discard
     * confirmation asks. False with no draft and on a freshly entered one,
     * true once either angle has moved off the value the edit started from.
     */
    isDraftDirty(): boolean {
        const draft = this._draft;
        if (!draft?.baseline) return false;

        return (
            Math.abs(draft.angles.craniocaudal - draft.baseline.craniocaudal) > DIRTY_ANGLE_EPSILON_DEG ||
            Math.abs(draft.angles.oblique - draft.baseline.oblique) > DIRTY_ANGLE_EPSILON_DEG
        );
    }

    canCommitDraft(): boolean {
        if (!this._draft) return false;

        return this._draft.target === 'new' || this.isDraftDirty();
    }

    /**
     * Ask the camera side to commit the draft. React consumers call this
     * (they have no viewport access) — CarmCameraBinding answers by reading
     * the live camera state and calling commitEdit with it.
     */
    requestCommitEdit(): void {
        if (!this._draft) return;
        this.broadcast(this.EVENTS.EDIT_COMMIT_REQUESTED, undefined);
    }

    /**
     * Commit the draft into its target view. The stored angles are the draft
     * values the user validated in the panel, never recomputed from the
     * camera — camera-derived angles are degenerate at oblique = ±90° and
     * unrecoverable past it (see computeCarmAngles).
     */
    commitEdit(cameraState: CameraState): void {
        const draft = this._draft;
        if (!draft) return;

        this._draft = null;
        this.unsubscribeEditInterruptions();

        if (draft.target === 'new') {
            this.addView(cameraState, draft.angles);
        } else {
            this.updateView(draft.target, cameraState, draft.angles);
        }

        this.broadcast(this.EVENTS.EDIT_COMMITTED, undefined);
    }

    /** Abandon the draft. No-op when no edit is active. */
    cancelEdit(): void {
        if (!this._draft) return;

        this._draft = null;
        this.unsubscribeEditInterruptions();
        this.broadcast(this.EVENTS.EDIT_CANCELLED, undefined);
    }

    /**
     * A draft survives tool switches and snapshot captures, but is abandoned
     * when the camera is reset to the frontal view or the viewport layout is
     * cleared (the cancel must run before teardown, while the 3D viewport is
     * still alive). Subscribed on beginEdit, torn down with the draft — so
     * these sibling services are only resolved once edits actually happen.
     */
    private subscribeEditInterruptions(): void {
        const services = this.context.services;
        const viewports = services.getService<ViewportService>(ViewportService.SERVICE_NAME);
        const layout = services.getService<ViewportLayoutService>(ViewportLayoutService.SERVICE_NAME);

        this._editInterruptionUnsubs.push(
            viewports.subscribe(viewports.EVENTS.CAMERA_RESET, () => this.cancelEdit()),
            layout.subscribe(layout.EVENTS.LAYOUT_CLEARING, () => this.cancelEdit())
        );
    }

    private unsubscribeEditInterruptions(): void {
        this._editInterruptionUnsubs.forEach((unsub) => unsub());
        this._editInterruptionUnsubs.length = 0;
    }

    // ========================================================================
    // Mutations
    // ========================================================================

    /**
     * Update an existing view with the committed camera state and draft
     * angles. Only reachable through commitEdit — beginEdit validated the
     * index, and every mutation that could invalidate it cancels the draft.
     */
    private updateView(index: number, cameraState: CameraState, angles: CarmAngles): void {
        const existing = this._views[index];

        const transform = cameraTransformFromState(cameraState);
        const updated = new CarmView(
            existing.id,
            existing.name,
            angles.craniocaudal,
            angles.oblique,
            transform
        );

        this._views[index] = updated;

        if (this.isPrecomputed(index)) {
            this._editedIndices.add(index);
        }

        this.broadcast(this.EVENTS.VIEW_UPDATED, { index });
    }

    /**
     * Add a new custom view with the committed camera state and draft angles.
     * Auto-selects the new view after adding.
     */
    private addView(cameraState: CameraState, angles: CarmAngles): void {
        this._customCounter++;
        const id = `custom-${this._customCounter}`;
        const name = `Custom ${this._customCounter}`;

        const transform = cameraTransformFromState(cameraState);
        const view = new CarmView(id, name, angles.craniocaudal, angles.oblique, transform);

        this._views.push(view);

        const newIndex = this._views.length - 1;

        this._selectedIndex = newIndex;

        this.broadcast(this.EVENTS.VIEW_ADDED, { index: newIndex });
        this.broadcast(this.EVENTS.SELECTION_CHANGED, {
            selectedIndex: newIndex,
            cameraTransform: view.cameraTransform,
        });
    }

    /**
     * Delete a custom view. Pre-computed views cannot be deleted.
     */
    deleteView(index: number): void {
        if (this.isPrecomputed(index)) return;
        if (index < 0 || index >= this._views.length) return;

        // Deleting shifts indices — an active draft would go stale
        this.cancelEdit();

        const wasSelected = this._selectedIndex === index;
        this._views.splice(index, 1);

        // Adjust selection
        if (wasSelected) {
            this._selectedIndex = null;
            this.broadcast(this.EVENTS.SELECTION_CHANGED, {
                selectedIndex: null,
                cameraTransform: null,
            });
        } else if (this._selectedIndex !== null && this._selectedIndex > index) {
            this._selectedIndex--;
        }

        this.broadcast(this.EVENTS.VIEW_DELETED, { index });
    }

    /**
     * Rename a view.
     */
    renameView(index: number, name: string): void {
        const existing = this._views[index];
        if (!existing) {
            console.warn(`[CarmViewService] renameView: no view at index ${index}`);
            return;
        }

        const trimmed = name.trim();
        if (!trimmed || trimmed === existing.name) return;

        this._views[index] = new CarmView(
            existing.id,
            trimmed,
            existing.craniocaudal,
            existing.oblique,
            existing.cameraTransform
        );

        if (this.isPrecomputed(index)) {
            this._editedIndices.add(index);
        }

        this.broadcast(this.EVENTS.VIEW_RENAMED, { index });
    }

    /**
     * Reset a pre-computed view to its original values from vascular description.
     */
    resetView(index: number): void {
        if (!this.isPrecomputed(index)) return;

        // Not offered during an edit, but stay safe if called anyway
        if (this._draft?.target === index) {
            this.cancelEdit();
        }

        const original = this._originalViews[index];
        if (!original) {
            console.warn(`[CarmViewService] resetView: no original view at index ${index}`);
            return;
        }

        this._views[index] = original;
        this._editedIndices.delete(index);

        this.broadcast(this.EVENTS.VIEW_RESET, { index });
    }

    // ========================================================================
    // Persistence
    // ========================================================================

    serializeCarmViews(): {
        edits: ICarmViewEditDTO[];
        custom: ICarmViewCustomDTO[];
        nextCustomId: number;
    } {
        const edits: ICarmViewEditDTO[] = [];
        for (const idx of this._editedIndices) {
            const view = this._views[idx];
            if (!view) continue;
            edits.push({
                originalIndex: idx,
                name: view.name,
                craniocaudal: view.craniocaudal,
                oblique: view.oblique,
                cameraTransform: view.cameraTransform,
            });
        }

        const custom: ICarmViewCustomDTO[] = [];
        for (let i = this._precomputedCount; i < this._views.length; i++) {
            const view = this._views[i];
            custom.push({
                id: view.id,
                name: view.name,
                craniocaudal: view.craniocaudal,
                oblique: view.oblique,
                cameraTransform: view.cameraTransform,
            });
        }

        return { edits, custom, nextCustomId: this._customCounter };
    }

    loadCarmViews(
        edits: readonly ICarmViewEditDTO[],
        custom: readonly ICarmViewCustomDTO[],
        nextCustomId: number
    ): void {
        this.cancelEdit();

        // Reset to pre-computed baseline (idempotent — safe to call multiple times)
        this._views.length = this._precomputedCount;
        for (let i = 0; i < this._precomputedCount; i++) {
            this._views[i] = this._originalViews[i];
        }
        this._editedIndices.clear();
        this._customCounter = nextCustomId;

        // Apply edits to pre-computed views
        for (const edit of edits) {
            if (edit.originalIndex >= 0 && edit.originalIndex < this._precomputedCount) {
                this._views[edit.originalIndex] = new CarmView(
                    this._originalViews[edit.originalIndex].id,
                    edit.name,
                    edit.craniocaudal,
                    edit.oblique,
                    edit.cameraTransform
                );
                this._editedIndices.add(edit.originalIndex);
            }
        }

        // Append custom views with their persisted IDs
        for (const c of custom) {
            this._views.push(new CarmView(c.id, c.name, c.craniocaudal, c.oblique, c.cameraTransform));
        }

        this.broadcast(this.EVENTS.VIEWS_INITIALIZED, undefined);
    }

    // ========================================================================
    // Lifecycle
    // ========================================================================

    override reset(): void {
        this.cancelEdit();
        this._originalViews = [];
        this._views = [];
        this._precomputedCount = 0;
        this._editedIndices.clear();
        this._customCounter = 0;
        this._selectedIndex = null;
        super.reset();
    }
}

/** Payload type for SELECTION_CHANGED events. */
export interface CarmSelectionChangedPayload {
    selectedIndex: number | null;
    cameraTransform: ICameraTransform | null;
}

/** Payload type for EDIT_STARTED events. */
export interface CarmEditStartedPayload {
    target: CarmEditTarget;
}

/** Payload type for EDIT_CHANGED events. */
export interface CarmEditChangedPayload {
    angles: CarmAngles;
}
